"use client";

import { Check } from "lucide-react";
import { Container } from "@/components/ui/Container";
import { MotionWrapper } from "@/components/ui/MotionWrapper";
import { AnimatedCounter } from "@/components/ui/AnimatedCounter";
import { useAutoAdvance, usePrefersReducedMotion } from "@/lib/motion";
import { metrics, credibilityLine } from "@/content/metrics";
import { company } from "@/content/company";
import { useLocale } from "@/i18n/locale";

// Réplica adaptada da seção "Built for scale" da livekit.com — mesma estrutura
// (badges, estatísticas, visual circular central, lista de regiões), com
// fonte, cores e conteúdo trocados para a marca e os números reais da SAT Labs.

const stateNames: Record<string, string> = {
  RS: "Rio Grande do Sul",
  SC: "Santa Catarina",
  PR: "Paraná",
  MS: "Mato Grosso do Sul",
  SP: "São Paulo",
  RJ: "Rio de Janeiro",
  MG: "Minas Gerais",
  AL: "Alagoas",
  GO: "Goiás",
  MA: "Maranhão",
  CE: "Ceará",
  AM: "Amazonas",
  AC: "Acre",
};

const badges = ["LGPD", "Monitoramento 24/7", "Suporte especializado"];

// Posição de cada estado no globo (projeção aproximada, centrada no Brasil),
// na mesma ordem de company.states — cada ponto corresponde 1:1 ao item da
// lista ao lado, então o ponto ativo no globo sempre é o estado em destaque.
const stateCoords: Record<string, { x: number; y: number }> = {
  RS: { x: 51, y: 86 },
  SC: { x: 57, y: 80 },
  PR: { x: 56, y: 74 },
  MS: { x: 44, y: 61 },
  SP: { x: 62, y: 69 },
  RJ: { x: 69, y: 67 },
  MG: { x: 68, y: 60 },
  AL: { x: 86, y: 33 },
  GO: { x: 56, y: 51 },
  MA: { x: 67, y: 14 },
  CE: { x: 80, y: 17 },
  AM: { x: 32, y: 16 },
  AC: { x: 14, y: 34 },
};

const AUTOPLAY_MS = 2200;

export function BuiltForScale() {
  const locale = useLocale();
  const reduced = usePrefersReducedMotion();
  const states = company.states;
  const [activeIndex] = useAutoAdvance(states.length, AUTOPLAY_MS);

  const [usuarios, clientes, , receita] = metrics;

  return (
    <section className="relative flex flex-col items-center gap-10 overflow-hidden py-20 sm:py-28">
      <Container className="relative z-10">
        <MotionWrapper className="space-y-2 text-center">
          <p className="font-mono text-sm font-bold uppercase tracking-[0.2em] text-text-muted">
            Construído para escalar
          </p>
          <h2 className="text-pretty text-3xl font-light tracking-tight sm:text-4xl">
            <span
              className="text-accent-lime"
              style={{ filter: "drop-shadow(0 0 8px rgba(153,255,0,0.35))" }}
            >
              {locale === "en" ? "Enterprise-grade" : "Infraestrutura"}
            </span>{" "}
            {locale === "pt" && <span className="text-text-primary">de nível</span>}
            <br />
            <span className="text-text-primary">
              {locale === "en" ? "infrastructure" : "empresarial"}
            </span>
          </h2>
        </MotionWrapper>
      </Container>

      <div className="relative w-full">
        <div
          aria-hidden
          className="bg-grid absolute inset-x-0 top-1/2 h-[420px] -translate-y-1/2"
        />

        <div className="relative z-10 mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-10 px-5 sm:px-8 lg:grid-cols-[220px_1fr_220px] lg:gap-4 lg:px-12">
          {/* coluna esquerda — selos e métricas */}
          <div className="mx-auto flex w-full max-w-xs flex-col gap-6 lg:mx-0">
            <div className="space-y-2.5">
              {badges.map((label) => (
                <div key={label} className="flex items-center gap-3">
                  <div className="flex gap-[3px]">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <span
                        key={i}
                        className="h-2.5 w-[3px] rounded-[1px] bg-accent-lime"
                        style={{ boxShadow: "0 1px 3px rgba(153,255,0,0.5)" }}
                      />
                    ))}
                  </div>
                  <span className="flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.06em] text-text-primary">
                    {label} <Check className="h-3 w-3 text-accent-lime" aria-hidden />
                  </span>
                </div>
              ))}
            </div>

            <StatRow value={usuarios} />
            <StatRow value={clientes} />
            <StatRow value={receita} />
          </div>

          {/* centro — globo */}
          <div className="relative mx-auto flex h-[320px] w-[320px] items-center justify-center sm:h-[420px] sm:w-[420px]">
            <div
              className={`absolute inset-0 rounded-full ${reduced ? "" : "animate-spin-slow"}`}
              style={{
                background:
                  "conic-gradient(from 0deg, transparent 0%, transparent 40%, var(--accent-lime) 50%, transparent 60%, transparent 100%)",
              }}
            />
            <div className="absolute inset-[6px] overflow-hidden rounded-full border border-line bg-bg-raised">
              {/* sombreamento de esfera — luz vindo do canto superior esquerdo */}
              <div
                aria-hidden
                className="absolute inset-0"
                style={{
                  background:
                    "radial-gradient(circle at 32% 28%, rgba(61,139,255,0.22), transparent 45%), radial-gradient(circle at 68% 78%, rgba(0,0,0,0.55), transparent 60%)",
                }}
              />

              <svg aria-hidden viewBox="0 0 100 100" className="absolute inset-0 h-full w-full">
                {/* meridianos */}
                {[12, 24, 36].map((rx) => (
                  <ellipse
                    key={`m-${rx}`}
                    cx={50}
                    cy={50}
                    rx={rx}
                    ry={42}
                    fill="none"
                    stroke="var(--line)"
                    strokeWidth={0.5}
                  />
                ))}
                {/* paralelos */}
                {[
                  { cy: 50, rx: 42, ry: 2.5 },
                  { cy: 35, rx: 38, ry: 5 },
                  { cy: 65, rx: 38, ry: 5 },
                  { cy: 21, rx: 25, ry: 4 },
                  { cy: 79, rx: 25, ry: 4 },
                ].map((p, i) => (
                  <ellipse
                    key={`p-${i}`}
                    cx={50}
                    cy={p.cy}
                    rx={p.rx}
                    ry={p.ry}
                    fill="none"
                    stroke="var(--line)"
                    strokeWidth={0.5}
                  />
                ))}
                {/* contorno da esfera */}
                <circle cx={50} cy={50} r={42} fill="none" stroke="var(--line)" strokeWidth={0.75} />

                {/* um ponto por estado — sincronizado com a lista ao lado */}
                {states.map((uf, i) => {
                  const point = stateCoords[uf];
                  if (!point) return null;
                  const active = i === activeIndex;
                  return (
                    <circle
                      key={uf}
                      cx={point.x}
                      cy={point.y}
                      r={active ? 2.2 : 1}
                      className={active ? "fill-accent-lime" : "fill-accent-blue"}
                      opacity={active ? 1 : 0.55}
                      style={
                        active
                          ? { filter: "drop-shadow(0 0 5px var(--accent-lime))" }
                          : undefined
                      }
                    >
                      {active && !reduced && (
                        <animate attributeName="r" values="1.8;2.6;1.8" dur="1.6s" repeatCount="indefinite" />
                      )}
                    </circle>
                  );
                })}
              </svg>
            </div>
            <p className="absolute bottom-6 z-10 font-mono text-xs uppercase tracking-[0.1em] text-text-muted">
              Operação SAT Labs
            </p>
          </div>

          {/* coluna direita — estados atendidos */}
          <div className="mx-auto flex w-full max-w-xs flex-col gap-6 lg:mx-0 lg:items-end">
            <div className="text-center lg:text-right">
              <p className="font-mono text-lg font-normal leading-tight text-text-primary">
                {states.length}+
              </p>
              <p className="font-mono text-[11px] font-bold uppercase tracking-[0.06em] text-text-muted">
                Estados atendidos
              </p>
            </div>

            <ul className="flex flex-col gap-1">
              {states.map((uf, i) => {
                const active = i === activeIndex;
                return (
                  <li key={uf}>
                    <div className="relative">
                      {active && (
                        <>
                          <span className="absolute left-0 top-0 h-2 w-2 border-l-2 border-t-2 border-accent-lime" aria-hidden />
                          <span className="absolute bottom-0 left-0 h-2 w-2 border-b-2 border-l-2 border-accent-lime" aria-hidden />
                          <span className="absolute right-0 top-0 h-2 w-2 border-r-2 border-t-2 border-accent-lime" aria-hidden />
                          <span className="absolute bottom-0 right-0 h-2 w-2 border-b-2 border-r-2 border-accent-lime" aria-hidden />
                        </>
                      )}
                      <p
                        className={`px-4 py-1 text-center font-mono text-xs font-bold uppercase leading-5 tracking-[0.04em] transition-colors duration-150 lg:text-right ${
                          active ? "text-accent-lime" : "text-text-muted"
                        }`}
                      >
                        {stateNames[uf] ?? uf}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>

      <p className="relative z-10 font-mono text-xs uppercase tracking-[0.12em] text-text-muted">
        {credibilityLine}
      </p>
    </section>
  );
}

function StatRow({ value }: { value: (typeof metrics)[number] }) {
  return (
    <div>
      <p className="font-mono text-lg font-normal leading-tight text-text-primary">
        <AnimatedCounter value={value.value} prefix={value.prefix} suffix={value.suffix} />
      </p>
      <p className="font-mono text-[11px] font-bold uppercase tracking-[0.06em] text-text-muted">
        {value.label}
      </p>
    </div>
  );
}
